<script>
/* global FB */
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import instagramClient from 'dashboard/api/channel/instagramClient';
import * as Sentry from '@sentry/vue';

const MESSENGER_SCOPES =
  'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages';
const INSTAGRAM_COMMENTS_SCOPE = 'instagram_manage_comments';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      commentsEnabled: false,
      isUpdating: false,
      isReauthorizing: false,
    };
  },
  computed: {
    commentsFeatureEnabled() {
      return window.chatwootConfig?.enableInstagramComments === 'true';
    },
    channelAttributeName() {
      return this.isAnInstagramChannel
        ? 'comments_enabled'
        : 'instagram_comments_enabled';
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler() {
        this.commentsEnabled = this.inbox.instagram_comments_enabled || false;
      },
    },
  },
  mounted() {
    if (!this.isAnInstagramChannel) {
      window.fbAsyncInit = this.runFBInit;
    }
  },
  methods: {
    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },
    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },
    async updateCommentsSetting() {
      const wasEnabled = Boolean(this.inbox.instagram_comments_enabled);
      this.isUpdating = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            [this.channelAttributeName]: this.commentsEnabled,
          },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        if (this.commentsEnabled && !wasEnabled) {
          await this.startCommentsReauthorize();
        }
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdating = false;
      }
    },
    async startCommentsReauthorize() {
      this.isReauthorizing = true;
      try {
        if (this.isAnInstagramChannel) {
          await this.reauthorizeDirectInstagram();
        } else {
          await this.loadFBsdk();
          this.runFBInit();
          this.tryFacebookCommentsLogin();
        }
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      } finally {
        this.isReauthorizing = false;
      }
    },
    async reauthorizeDirectInstagram() {
      const {
        data: { url },
      } = await instagramClient.generateAuthorization({
        enable_comments: true,
      });
      window.location.href = url;
    },
    tryFacebookCommentsLogin() {
      FB.login(
        response => {
          if (response.status === 'connected') {
            this.reauthorizeFBPage(response.authResponse.accessToken);
          } else {
            useAlert(
              this.$t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.REAUTHORIZE_REQUIRED')
            );
          }
        },
        {
          scope: `${MESSENGER_SCOPES},${INSTAGRAM_COMMENTS_SCOPE}`,
          auth_type: 'reauthorize',
        }
      );
    },
    async reauthorizeFBPage(omniauthToken) {
      try {
        await this.$store.dispatch('inboxes/reauthorizeFacebookPage', {
          omniauthToken,
          inboxId: this.inbox.id,
        });
        useAlert(this.$t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <SettingsSection
    v-if="commentsFeatureEnabled"
    :title="$t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.TITLE')"
    :sub-title="$t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.HELP')"
  >
    <div class="flex flex-col gap-3">
      <label class="flex gap-2 items-center">
        <input v-model="commentsEnabled" type="checkbox" />
        <span>{{ $t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.ENABLE') }}</span>
      </label>
      <p class="text-xs italic text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.REAUTHORIZE_HELP') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isUpdating"
          @click="updateCommentsSetting"
        />
        <NextButton
          v-if="commentsEnabled"
          variant="faded"
          :label="$t('INBOX_MGMT.ADD.INSTAGRAM.COMMENTS.REAUTHORIZE')"
          :is-loading="isReauthorizing"
          @click="startCommentsReauthorize"
        />
      </div>
    </div>
  </SettingsSection>
</template>
